// =============================================================================
// src/data/analytics-feed-sort.ts — Vocabulario de ordenacao do feed (puro).
//
// 2026-09-20: o feed deixa de ser reordenado no DOM. Ate aqui existiam dois
// botoes ("Mais recentes" / "Mais populares") que mexiam a ordem dos ~56 cards
// JA renderizados — e esses cards eram uma amostra enviesada (so 12 dos baldes
// vinham por data), entao "mais recentes" ordenava por data um conjunto
// escolhido por popularidade. A ordenacao passa a ir no `order_by` da consulta.
//
// Modulo separado do builder de query de proposito: analytics-view-state precisa
// do parse e nao pode importar a camada de GraphQL sem criar ciclo.
// =============================================================================

/** Criterios oferecidos ao operador. `vistas` substituiu "alcance" — ver DESIGN/plano:
 *  so 23,6% do feed tem sinal de alcance e o Instagram nao tem seguidores coletados. */
export type AnalyticsFeedSortField = 'data' | 'engajamento' | 'vistas' | 'sentimento';

export type AnalyticsFeedSortDirection = 'desc' | 'asc';

/** Ordenacao ativa, no formato `<criterio>:<direcao>` (o mesmo que vai ao storage). */
export type AnalyticsFeedSort = `${AnalyticsFeedSortField}:${AnalyticsFeedSortDirection}`;

export const ANALYTICS_FEED_SORTS: readonly AnalyticsFeedSort[] = [
  'data:desc', 'data:asc',
  'engajamento:desc', 'engajamento:asc',
  'vistas:desc', 'vistas:asc',
  'sentimento:desc', 'sentimento:asc',
];

/** Mais recentes primeiro: o que o feed sempre prometeu no rotulo. */
export const ANALYTICS_DEFAULT_FEED_SORT: AnalyticsFeedSort = 'data:desc';

/** Rotulo de cada opcao, na lingua do produto (nunca "asc"/"desc" na tela). */
export const ANALYTICS_FEED_SORT_LABELS: Readonly<Record<AnalyticsFeedSort, string>> = {
  'data:desc': 'Mais recentes',
  'data:asc': 'Mais antigas',
  'engajamento:desc': 'Maior engajamento',
  'engajamento:asc': 'Menor engajamento',
  'vistas:desc': 'Mais vistas',
  'vistas:asc': 'Menos vistas',
  'sentimento:desc': 'Mais positivas',
  'sentimento:asc': 'Mais negativas',
};

/** Rotulo do grupo no seletor (um `optgroup` por criterio). */
export const ANALYTICS_FEED_SORT_FIELD_LABELS: Readonly<Record<AnalyticsFeedSortField, string>> = {
  data: 'Data',
  engajamento: 'Engajamento',
  vistas: 'Visualizacoes',
  sentimento: 'Sentimento',
};

/** Criterios sem dado na base demonstrativa — as opcoes nascem desabilitadas. */
const ANALYTICS_LIVE_ONLY_SORT_FIELDS: readonly AnalyticsFeedSortField[] = ['sentimento'];

/**
 * Valor do seletor/storage → ordenacao tipada; null para valor desconhecido.
 * Espelha parseAnalyticsFeedScope (analytics-scope-where) e parseAnalyticsPeriod:
 * quem chama decide o default, para o campo estragado no storage nao derrubar os
 * outros. Vale tambem para 'recent'/'popular', o vocabulario do sort ANTIGO.
 *
 * @example parseAnalyticsFeedSort('engajamento:desc') // 'engajamento:desc'
 */
export function parseAnalyticsFeedSort(value: string | null | undefined): AnalyticsFeedSort | null {
  return ANALYTICS_FEED_SORTS.find((sort) => sort === value) ?? null;
}

/**
 * Criterio de uma ordenacao, sem repetir a tabela de rotulos.
 *
 * @example analyticsFeedSortField('vistas:asc') // 'vistas'
 */
export function analyticsFeedSortField(sort: AnalyticsFeedSort): AnalyticsFeedSortField {
  return sort.split(':')[0] as AnalyticsFeedSortField;
}

/**
 * A ordenacao depende de coluna que so existe na coleta viva?
 *
 * Sentimento chegou ao feed na migration 031; na base demonstrativa a coluna nao
 * tem valor, e ordenar por ela devolveria uma ordem inventada. Opcao desabilitada
 * e honesta — mesmo tratamento que a tab de sentimento do grafico ja recebe.
 *
 * @example analyticsFeedSortIsLiveOnly('sentimento:desc') // true
 */
export function analyticsFeedSortIsLiveOnly(sort: AnalyticsFeedSort): boolean {
  return ANALYTICS_LIVE_ONLY_SORT_FIELDS.includes(analyticsFeedSortField(sort));
}
